// Angular
import { Observable, takeUntil } from 'rxjs';
import { AfterViewInit, ChangeDetectorRef, Component, HostBinding, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// Libraries

import _ from 'lodash-es';
import { MatDrawer } from '@angular/material/sidenav';

import { Select, Store } from '@ngxs/store';

// Models
import { AppCleanup, DrawerModel } from '../../../../shared/models';
import { Job } from '../../models';
import { JobUpdate } from '../../models';
import { JobCommentDto } from '../../models/job-comment-dto';

// Services
import { JobService } from '../../services/job.service';
import { DashboardService } from '../dashboard/services/dashboard.service';
import { SubheaderService } from '../../../../shared/components/subheader/services/subheader.service';
import { DrawerService } from '../../../../shared/services/drawer.service';
import { JobOverviewService } from '../../services/job-overview.service';
import { AuthService } from '../../../auth';

// Store
import { StorageState } from '../../../../shared/state/storage.state';
import { StorageActions } from '../../../../shared/state/storage.action';

// Enums
import { DrawerModeType } from '../../../../shared/enums/drawer-mode.enum';
import { Roles } from '../../../auth/models/roles.enum';
import { ChangeType } from '../../enums/change-type.enum';

// Helpers
import { UtilityHelper } from '../../../../shared/helpers';
import { ChartState } from '../job-chart/job-chart.component';

@Component({
	selector: 'job-overview',
	templateUrl: './job-overview.component.html',
	styleUrls: ['job-overview.component.scss'],
})
export class JobOverviewComponent extends AppCleanup implements OnInit, AfterViewInit, OnDestroy {
	@HostBinding('class') classes = 'component-container';
	@Select(StorageState.commentDrawerSettings) commentDrawerSettings$: Observable<DrawerModel>;
	commentDrawerSettings: DrawerModel;

	@Select(StorageState.commentFontSize) commentFontSize$: Observable<string>;
	commentFontSize: number = 12;

	jobKey: string; /**Equal to Input key if set, otherwise retrieved from route snapshot id */
	jobId: number;
	job: Job;
	jobComments: JobCommentDto[];
	isPublic: boolean; /**If true, the Job is viewable to the Public through a snapshot key. If false, you must be logged in to see the job*/
	isScreenLarge: boolean = false;
	isAdmin: boolean;
	chartState: ChartState;
	linkLoadFailed: boolean = false;
	
	@ViewChild('commentDrawer', { static: false }) drawer: MatDrawer;

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.checkDrawer();
	}

	constructor(
		public dashboardService: DashboardService,
		private subheaderService: SubheaderService,
		public jobService: JobService,
		public route: ActivatedRoute,
		private changeDetectorRef: ChangeDetectorRef,
		private drawerService: DrawerService,
		private store: Store,
		private authService: AuthService,
		private jobOverviewService: JobOverviewService
	) {
		super();
	}

	ngOnInit() {
		this.subheaderService.disabled$.next(false);
		this.isPublic = this.route.snapshot.data && this.route.snapshot.data.hasOwnProperty('public') && this.route.snapshot.data.public;
		this.jobKey = this.route.snapshot.paramMap.get('jobId');
		
		if (this.isPublic) {
			this.jobService
				.getJobIdFromKey(this.jobKey)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: (jobId) => {
						if (jobId) {
							this.jobId = jobId;
						}
					},
					error: (error) => {
						this.linkLoadFailed = true;
						// Link is expired, disabled, or invalid.
						// Let child components show the error message.
						console.error('Failed to get job ID from link key:', error);
					}
				});
		}
		
		this.authService
			.checkAccess([Roles.TenantAdmin, Roles.SuperAdmin, Roles.Admin], () => {
				return true;
			})
			.subscribe((isAdmin) => {
				this.isAdmin = isAdmin;
			});

		// NEW: Use job-specific stream instead of global stream with filtering
		this.jobService
			.getJobStream(this.jobKey)
			.pipe(takeUntil(this.destroy))
			.subscribe((jobUpdate: JobUpdate) => {
				if (jobUpdate && (jobUpdate.changeType === ChangeType.INITIAL || jobUpdate.changeType === ChangeType.JOB_COMMENT || jobUpdate.changeType === ChangeType.JOB_CHANGE)) {
					this.job = new Job(jobUpdate.job);
					if (this.job?.jobCommentReturn) {
						this.jobComments = this.job.jobCommentReturn.jobComments.filter((jc) => !jc.isDeleted && (!jc.hideInPortal || this.isAdmin));
					} else {
						this.jobComments = [];
					}
				}
			});

		// LEGACY: Keep fallback for backward compatibility during migration
		// this.jobService.jobChange$.pipe(takeUntil(this.destroy)).subscribe((jobChange) => {
		// 	if (jobChange && (jobChange.changeType === ChangeType.INITIAL || jobChange.changeType === ChangeType.JOB_COMMENT || jobChange.changeType === ChangeType.JOB_CHANGE) && jobChange.job.key === this.jobKey && !this.job) {
		// 		// Only fallback if job-specific stream hasn't loaded yet
		// 		this.job = new Job(jobChange.job);
		// 		if (this.job?.jobCommentReturn) {
		// 			this.jobComments = this.job.jobCommentReturn.jobComments.filter((jc) => !jc.isDeleted && (!jc.hideInPortal || this.isAdmin));
		// 		} else {
		// 			this.jobComments = [];
		// 		}
		// 	}
		// });

		this.commentDrawerSettings$.pipe(takeUntil(this.destroy)).subscribe((commentDrawerSettings) => {
			if (commentDrawerSettings) {
				this.commentDrawerSettings = UtilityHelper.deepCopy(commentDrawerSettings);
				this.changeDetectorRef.detectChanges(); // Fixes NG0100 warning
			}
		});

		this.commentFontSize$.pipe(takeUntil(this.destroy)).subscribe((fontSize) => {
			if (fontSize) {
				let commentFontSize = fontSize;
				this.commentFontSize = parseInt(commentFontSize);
			}
		});

		this.jobOverviewService.chartState$.pipe(takeUntil(this.destroy)).subscribe((chartState) => {
			this.chartState = chartState;
		});
	}

	ngAfterViewInit() {
		this.drawerService.setCommentDrawer(this.drawer);

		setTimeout(() => {
			this.checkDrawer();
		}, 300);
	}

	ngOnDestroy() {
		this.jobOverviewService.setChartState(null);
		super.ngOnDestroy();
	}

	checkDrawer() {
		this.isScreenLarge = window.innerWidth >= 1200;

		if (this.isScreenLarge) {
			if (this.commentDrawerSettings.pinned) {
				this.commentDrawerSettings.mode = DrawerModeType.SIDE;
				this.drawerService.openCommentDrawer();
			}
		} else {
			this.commentDrawerSettings.mode = DrawerModeType.OVER;
			this.drawerService.closeCommentDrawer();
		}

		this.store.dispatch(new StorageActions.SetCommentDrawerSettings(this.commentDrawerSettings));
		this.changeDetectorRef.markForCheck();
	}

	changeDrawerMode() {
		if (this.commentDrawerSettings.mode === DrawerModeType.OVER) {
			this.commentDrawerSettings.mode = DrawerModeType.SIDE;
			this.commentDrawerSettings.pinned = true;
		} else if (this.commentDrawerSettings.mode === DrawerModeType.SIDE) {
			this.commentDrawerSettings.mode = DrawerModeType.OVER;
			this.commentDrawerSettings.pinned = false;
			this.drawerService.commentDrawer.close();
		}

		this.store.dispatch(new StorageActions.SetCommentDrawerSettings(this.commentDrawerSettings));
		this.changeDetectorRef.markForCheck();
	}

	decrementFontSizes() {
		if (this.commentFontSize <= 8) return;
		this.commentFontSize -= 2;
		this.store.dispatch(new StorageActions.SetCommentFontSize(String(this.commentFontSize)));
	}

	incrementFontSizes() {
		if (this.commentFontSize >= 66) return;
		this.commentFontSize += 2;
		this.store.dispatch(new StorageActions.SetCommentFontSize(String(this.commentFontSize)));
	}

	closeDrawer() {
		this.drawerService.closeCommentDrawer();
	}
}
